import { useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { ColorV2 } from "config/colors";
import { FontFamily, TypographyV2 } from "config/font";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";

interface AnalyticsChartProps {
  orders: Order[];
  timeReports: TimeReportDto[];
  period: string;
  loading: boolean;
}

interface ChartBucket {
  label: string;
  sales: number;
  tips: number;
  personnelCost: number;
  result: number;
}

// JS getDay() returns 0=Sun, 1=Mon…6=Sat → convert to 0=Mon…6=Sun
const jsDayToBucketIndex = (jsDay: number): number =>
  jsDay === 0 ? 6 : jsDay - 1;

// Order.timestamp is typed as Date but arrives as ISO string from the API
const parseTimestamp = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

const buildDayBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
): ChartBucket[] => {
  const buckets: ChartBucket[] = Array.from({ length: 24 }, (_, hour) => ({
    label: `${String(hour).padStart(2, "0")}:00`,
    sales: 0,
    tips: 0,
    personnelCost: 0,
    result: 0,
  }));

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const hour = d.getHours();
    buckets[hour].sales += order.amount;
    buckets[hour].tips += order.tip;
  }

  for (const report of timeReports) {
    if (!report.startTime || !report.endTime) continue;
    const startMs = new Date(report.startTime).getTime();
    const endMs = new Date(report.endTime).getTime();
    if (endMs <= startMs) continue;

    // Walk hour-by-hour, assigning proportional cost to each covered bucket
    let cursor = new Date(report.startTime);
    cursor.setMinutes(0, 0, 0);

    while (cursor.getTime() < endMs) {
      const bucketStart = Math.max(cursor.getTime(), startMs);
      const bucketEnd = Math.min(cursor.getTime() + 3_600_000, endMs);
      const fractionHours = (bucketEnd - bucketStart) / 3_600_000;
      const hour = cursor.getHours();
      buckets[hour].personnelCost += report.hourlyRate * fractionHours;
      cursor = new Date(cursor.getTime() + 3_600_000);
    }
  }

  for (const bucket of buckets) {
    bucket.result = bucket.sales - bucket.personnelCost;
  }

  return buckets;
};

const buildWeekBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  weekdayLabels: string[],
): ChartBucket[] => {
  const buckets: ChartBucket[] = Array.from({ length: 7 }, (_, i) => ({
    label: weekdayLabels[i],
    sales: 0,
    tips: 0,
    personnelCost: 0,
    result: 0,
  }));

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const idx = jsDayToBucketIndex(d.getDay());
    buckets[idx].sales += order.amount;
    buckets[idx].tips += order.tip;
  }

  for (const report of timeReports) {
    if (!report.startTime || !report.endTime) continue;
    const startMs = new Date(report.startTime).getTime();
    const endMs = new Date(report.endTime).getTime();
    if (endMs <= startMs) continue;
    const totalHours = (endMs - startMs) / 3_600_000;
    const idx = jsDayToBucketIndex(new Date(report.startTime).getDay());
    buckets[idx].personnelCost += report.hourlyRate * totalHours;
  }

  for (const bucket of buckets) {
    bucket.result = bucket.sales - bucket.personnelCost;
  }

  return buckets;
};

const SHARED_AXIS_PROPS = {
  tick: { fill: ColorV2.Blue4, fontSize: 12, fontFamily: FontFamily.SpaceGroteskRegular },
  tickLine: false as const,
};

const TOOLTIP_PROPS = {
  formatter: (value: unknown, name: unknown) => [
    typeof value === "number"
      ? `${Math.round(value).toLocaleString("sv-SE")} kr`
      : String(value),
    name,
  ],
  contentStyle: {
    background: ColorV2.White,
    border: `1.5px solid ${ColorV2.Blue5}`,
    borderRadius: "12px",
    fontSize: "13px",
    fontFamily: FontFamily.SpaceGroteskRegular,
  },
};

const LEGEND_PROPS = {
  wrapperStyle: {
    fontSize: "13px",
    color: ColorV2.Blue3,
    fontFamily: FontFamily.SpaceGroteskRegular,
  },
};

export const AnalyticsChart = ({
  orders,
  timeReports,
  period,
  loading,
}: AnalyticsChartProps) => {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState("bar");

  const weekdayLabels = [
    t("CompanyAnalytics.WeekdayMon"),
    t("CompanyAnalytics.WeekdayTue"),
    t("CompanyAnalytics.WeekdayWed"),
    t("CompanyAnalytics.WeekdayThu"),
    t("CompanyAnalytics.WeekdayFri"),
    t("CompanyAnalytics.WeekdaySat"),
    t("CompanyAnalytics.WeekdaySun"),
  ];

  const series = [
    { dataKey: "sales", name: t("CompanyAnalytics.Sales"), color: ColorV2.ElectricBlue },
    { dataKey: "personnelCost", name: t("CompanyAnalytics.PersonnelCost"), color: ColorV2.NegativeRed },
    { dataKey: "tips", name: t("CompanyAnalytics.Tips"), color: ColorV2.PositiveGreen },
  ];

  const chartTypeOptions = [
    { value: "bar", label: t("CompanyAnalytics.BarChart") },
    { value: "line", label: t("CompanyAnalytics.LineChart") },
  ];

  const data: ChartBucket[] =
    period === "week"
      ? buildWeekBuckets(orders, timeReports, weekdayLabels)
      : buildDayBuckets(orders, timeReports);

  const commonProps = {
    data,
    margin: { top: 8, right: 16, left: 16, bottom: 4 },
  };

  const commonChildren = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={ColorV2.Blue6} vertical={false} />
      <XAxis
        dataKey="label"
        {...SHARED_AXIS_PROPS}
        axisLine={{ stroke: ColorV2.Blue5 }}
      />
      <YAxis
        {...SHARED_AXIS_PROPS}
        axisLine={false}
        tickFormatter={(v: number) =>
          v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))
        }
      />
      <Tooltip {...TOOLTIP_PROPS} />
      <Legend {...LEGEND_PROPS} />
    </>
  );

  return (
    <ChartCard>
      <ChartHeader>
        <ChartTitle>{t("CompanyAnalytics.SalesAndCosts")}</ChartTitle>
        <SegmentController
          options={chartTypeOptions}
          value={chartType}
          onChange={setChartType}
        />
      </ChartHeader>
      {loading ? (
        <LoaderWrapper>
          <CircularProgress size={32} />
        </LoaderWrapper>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          {chartType === "line" ? (
            <LineChart {...commonProps}>
              {commonChildren}
              {series.map((s) => (
                <Line
                  key={s.dataKey}
                  type="monotone"
                  dataKey={s.dataKey}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          ) : (
            <ComposedChart {...commonProps} barCategoryGap="15%" barGap={2}>
              {commonChildren}
              {series.map((s) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name}
                  fill={s.color}
                  radius={[3, 3, 0, 0]}
                />
              ))}
              <Line
                type="monotone"
                dataKey="result"
                name={t("CompanyAnalytics.Result")}
                stroke={ColorV2.Blue1}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};

const ChartCard = styled.div`
  background: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChartTitle = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 280px;
`;
